import { type ReactNode } from "react"

interface CategoryFeature {
  icon: ReactNode
  title: string
  description: string
}

interface CategoryHeroCardProps {
  /** Category name displayed over the hero image */
  categoryName: string
  /** Short description displayed under the category name */
  categoryDescription: string
  /** Hero image URL — lifestyle photo of the category (e.g. French door fridge in a kitchen) */
  imageUrl: string
  /** Array of 4-6 key features to display in the right panel */
  features: CategoryFeature[]
  /** Additional classes on the outer wrapper */
  className?: string
}

/**
 * CategoryHeroCard — A 50/50 split hero card for hub/category pages.
 *
 * Left half: lifestyle image with dark gradient overlay, category name + description.
 * Right half: clean white panel with key features in a 2-column grid.
 */
export function CategoryHeroCard({
  categoryName,
  categoryDescription,
  imageUrl,
  features,
  className = "",
}: CategoryHeroCardProps) {
  return (
    <div
      className={`rounded-xl overflow-hidden border border-gray-100 shadow-sm flex flex-col md:flex-row ${className}`}
    >
      {/* ── Left: Hero Image ── */}
      <div className="relative w-full md:w-1/2 min-h-[280px] md:min-h-[360px]">
        {/* Background image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${imageUrl})` }}
        />
        {/* Dark gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/20" />

        {/* Category name + description, anchored bottom-left */}
        <div className="relative h-full flex flex-col justify-end p-6 md:p-8">
          <div className="flex items-start gap-3">
            {/* Orange accent bar */}
            <div className="w-[3px] min-h-[40px] bg-[#ff9951] rounded-full flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-white text-xl md:text-2xl font-bold leading-tight mb-1.5">
                {categoryName}
              </h2>
              <p className="text-white/75 text-sm leading-relaxed line-clamp-2">
                {categoryDescription}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right: Key Features ── */}
      <div className="w-full md:w-1/2 bg-white p-6 md:p-8 flex flex-col justify-center">
        <p className="text-xs font-bold tracking-widest text-gray-500 uppercase mb-5">
          Key Features
        </p>
        <div className="grid grid-cols-2 gap-5">
          {features.map((feature, i) => (
            <div key={i} className="flex flex-col gap-2">
              <div className="w-9 h-9 rounded-lg bg-[#0469f0]/10 flex items-center justify-center flex-shrink-0">
                {feature.icon}
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-900 leading-tight">
                  {feature.title}
                </p>
                <p className="text-xs text-gray-500 leading-relaxed mt-0.5 line-clamp-1">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
